import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';

import { registerStatus, role } from '../../../../constants/enum';
import { deleteFile, uploadFile } from '../../../aws/s3';
import { db } from '../../config';

import type { UserCredential } from 'firebase/auth';
import type {
  DocumentData,
  DocumentReference,
  QueryDocumentSnapshot,
  DocumentSnapshot
} from 'firebase/firestore';

export const usersRef = collection(db, 'users');

export const userDocument = (uid: string): DocumentReference<DocumentData> =>
  doc(usersRef, uid);

export const userSnapshot = async (uid: string): Promise<DocumentSnapshot> =>
  await getDoc(userDocument(uid));

export const userFindByCompanyId = async (
  companyId: string
): Promise<Array<QueryDocumentSnapshot<DocumentData>>> => {
  if (!companyId) return [];
  const user = query<DocumentData>(
    usersRef,
    where('companyId', '==', companyId),
    where('registerStatus', '==', registerStatus.finished)
  );
  const { docs } = await getDocs(user);
  return docs;
};

export const userFindByEmail = async (email: string): Promise<boolean> => {
  const user = query<DocumentData>(usersRef, where('email', '==', email));
  const { empty, size } = await getDocs(user);

  return !(empty || size >= 2);
};

export const createUserWhenSendemail = async (
  screenType: number,
  email: string
): Promise<void> => {
  const data = {
    created: new Date(),
    email,
    registerStatus: registerStatus.emailRegistered,
    role: role.admin,
    type: screenType,
    updated: new Date(),
    utm_campaign: window.localStorage.getItem('utm_campaign'),
    utm_content: window.localStorage.getItem('utm_content'),
    utm_id: window.localStorage.getItem('utm_id'),
    utm_medium: window.localStorage.getItem('utm_medium'),
    utm_source: window.localStorage.getItem('utm_source'),
    utm_term: window.localStorage.getItem('utm_term')
  };

  await addDoc(usersRef, data);
};

export const createStaffUserWhenSendemail = async (
  screenType: number,
  email: string,
  watch: (arg0: string) => any,
  companyId: string
): Promise<void> => {
  const data = {
    companyId,
    created: new Date(),
    email,
    inviteFlg: true,
    registerStatus: registerStatus.emailRegistered,
    role:
      (watch('role') as string | boolean | null) === 'true'
        ? role.admin
        : (watch('role') as string | boolean | null) === 'false'
        ? role.member
        : (watch('role') as string | boolean | null)
        ? role.admin
        : role.member,
    type: screenType,
    updated: new Date(),
    utm_campaign: window.localStorage.getItem('utm_campaign'),
    utm_content: window.localStorage.getItem('utm_content'),
    utm_id: window.localStorage.getItem('utm_id'),
    utm_medium: window.localStorage.getItem('utm_medium'),
    utm_source: window.localStorage.getItem('utm_source'),
    utm_term: window.localStorage.getItem('utm_term')
  };

  await addDoc(usersRef, data);
};

export const addPublisherUser = async (
  email: string | null,
  companyDoc: DocumentReference<DocumentData>,
  createdUser: UserCredential,
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  if (email) {
    email = email.split('?').join('');
  }
  const q = query(usersRef, where('email', '==', email));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);

  querySnapshot.docs.map(
    async (oldDoc: QueryDocumentSnapshot<DocumentData>): Promise<void> => {
      // 更新するusersコレクションのデータ
      const userData = {
        companyId: companyDoc.id,
        created: oldDoc.data().created as Date,
        email,
        name: watch('representativeName') as string,
        registerStatus: registerStatus.finished,
        role: oldDoc.data().role as number,
        type: oldDoc.data().type as number,
        uid: createdUser.user.uid,
        updated: new Date(),
        utm_campaign: oldDoc.data().utm_campaign as string,
        utm_content: oldDoc.data().utm_content as string,
        utm_id: oldDoc.data().utm_id as string,
        utm_medium: oldDoc.data().utm_medium as string,
        utm_source: oldDoc.data().utm_source as string,
        utm_term: oldDoc.data().utm_term as string
      };
      // Userドキュメントの置き換え（authのuidとusers.idを合わせる）
      await setDoc(doc(db, 'users', createdUser.user.uid), userData);
      await deleteDoc(doc(usersRef, oldDoc.id));
    }
  );
  return doc(usersRef, createdUser.user.uid);
};

export const addClientUser = async (
  email: string | null,
  companyDoc: DocumentReference<DocumentData>,
  createdUser: UserCredential,
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  if (email) {
    email = email.split('?').join('');
  }
  const q = query(usersRef, where('email', '==', email));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);

  querySnapshot.docs.map(
    async (oldDoc: QueryDocumentSnapshot<DocumentData>): Promise<void> => {
      // 更新するusersコレクションのデータ
      const userData = {
        companyId: companyDoc.id,
        created: oldDoc.data().created as Date,
        department: (watch('department') as string) || null,
        email,
        name: watch('representativeName') as string,
        registerStatus: registerStatus.passwordRegistered,
        role: oldDoc.data().role as number,
        type: oldDoc.data().type as number,
        uid: createdUser.user.uid,
        updated: new Date(),
        utm_campaign: oldDoc.data().utm_campaign as string,
        utm_content: oldDoc.data().utm_content as string,
        utm_id: oldDoc.data().utm_id as string,
        utm_medium: oldDoc.data().utm_medium as string,
        utm_source: oldDoc.data().utm_source as string,
        utm_term: oldDoc.data().utm_term as string
      };
      // Userドキュメントの置き換え（authのuidとusers.idを合わせる）
      await setDoc(doc(db, 'users', createdUser.user.uid), userData);
      await deleteDoc(doc(usersRef, oldDoc.id));
    }
  );
  return doc(db, 'users', createdUser.user.uid);
};

export const editUser = async (
  uid: string,
  watch: (arg0: string) => any
): Promise<void> => {
  const userData = {
    department: (watch('department') as string) || null,
    name: watch('representativeName') as string,
    updated: new Date()
  };

  const userDoc = doc(usersRef, uid);
  await updateDoc(userDoc, userData);
};

export const editPublisherUser = async (
  uid: string,
  watch: (arg0: string) => any
): Promise<void> => {
  const userData = {
    name: watch('representativeName') as string,
    updated: new Date()
  };

  const userDoc = doc(usersRef, uid);
  await updateDoc(userDoc, userData);
};

export const addAgentUser = async (
  email: string | null,
  companyDoc: DocumentReference<DocumentData>,
  createdUser: UserCredential,
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  if (email) {
    email = email.split('?').join('');
  }
  const q = query(usersRef, where('email', '==', email));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);

  querySnapshot.docs.map(
    async (oldDoc: QueryDocumentSnapshot<DocumentData>): Promise<void> => {
      // 更新するusersコレクションのデータ
      const userData = {
        companyId: companyDoc.id,
        created: oldDoc.data().created as Date,
        department: (watch('department') as string) || null,
        email,
        name: watch('representativeName') as string,
        registerStatus: registerStatus.passwordRegistered,
        role: oldDoc.data().role as number,
        type: oldDoc.data().type as number,
        uid: createdUser.user.uid,
        updated: new Date(),
        utm_campaign: oldDoc.data().utm_campaign as string,
        utm_content: oldDoc.data().utm_content as string,
        utm_id: oldDoc.data().utm_id as string,
        utm_medium: oldDoc.data().utm_medium as string,
        utm_source: oldDoc.data().utm_source as string,
        utm_term: oldDoc.data().utm_term as string
      };
      // Userドキュメントの置き換え（authのuidとusers.idを合わせる）
      await setDoc(doc(db, 'users', createdUser.user.uid), userData);
      await deleteDoc(doc(usersRef, oldDoc.id));
    }
  );
  return doc(db, 'users', createdUser.user.uid);
};

// 登録完了に変更
export const editUserToComplete = async (uid: string): Promise<void> => {
  const userData = {
    registerStatus: registerStatus.finished,
    updated: new Date()
  };
  await updateDoc(doc(db, 'users', uid), userData);
};

export const getUserInfo = async (
  uid?: string
): Promise<DocumentData | undefined> => {
  const userRef = doc(usersRef, uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.data();
  return userData;
};

export const editStaffUser = async (
  uid: string,
  watch: (arg0: string) => any,
  fileIcon: FileList | null
): Promise<void> => {
  const q = query(usersRef, where('uid', '==', uid));
  const querySnapshot = await getDocs(q);
  let oldFileIcon = '';
  querySnapshot.docs.forEach(
    (document: QueryDocumentSnapshot<DocumentData>): void => {
      oldFileIcon = document.data().iconId as string;
    }
  );

  const fileName = fileIcon !== null ? fileIcon[0].name : '';
  const icon = fileIcon
    ? `users/${uid}/profile/${Date.now()}_${fileName}`
    : null;

  let userData = {
    department: (watch('department') as string) || null,
    name: watch('name') as string,
    role:
      typeof watch('role') === 'boolean'
        ? watch('role')
          ? 1
          : 2
        : watch('role') === 'true'
        ? 1
        : 2,
    updated: new Date()
  };

  if (icon) {
    uploadFile(fileIcon, icon);
    userData = { ...userData, ...{ iconId: icon } };
    if (oldFileIcon) {
      deleteFile(oldFileIcon);
    }
  }

  const userDoc = doc(usersRef, uid);
  await updateDoc(userDoc, userData);
};

export const addStaffUser = async (
  email: string | null,
  createdUser: UserCredential,
  watch: (arg0: string) => any,
  fileIcon: FileList | null
): Promise<void> => {
  if (email) {
    email = email.split('?').join('');
  }

  const q = query(usersRef, where('email', '==', email));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);

  let user: DocumentReference<DocumentData>;

  querySnapshot.docs.map(
    async (oldDoc: QueryDocumentSnapshot<DocumentData>): Promise<void> => {
      // 更新するusersコレクションのデータ

      const userData = {
        companyId: oldDoc.data().companyId as string,
        created: oldDoc.data().created as Date,
        department: (watch('department') as string) || null,
        email,
        inviteFlg: oldDoc.data().inviteFlg as string,
        name: watch('name') as string,
        registerStatus: registerStatus.finished,
        role: oldDoc.data().role as string,
        type: oldDoc.data().type as number,
        uid: createdUser.user.uid,
        updated: new Date(),
        utm_campaign: oldDoc.data().utm_campaign as string,
        utm_content: oldDoc.data().utm_content as string,
        utm_id: oldDoc.data().utm_id as string,
        utm_medium: oldDoc.data().utm_medium as string,
        utm_source: oldDoc.data().utm_source as string,
        utm_term: oldDoc.data().utm_term as string
      };
      // Userドキュメントの置き換え（authのuidとusers.idを合わせる）
      await setDoc(doc(db, 'users', createdUser.user.uid), userData);
      await deleteDoc(doc(usersRef, oldDoc.id));

      user = doc(db, 'users', createdUser.user.uid);
      const userSnap = await getDoc(user);
      const newUid = userSnap.data()?.uid as string;
      const fileIconName = fileIcon !== null ? fileIcon[0].name : '';
      const icon =
        fileIcon !== null
          ? `users/${newUid}/profile/${Date.now()}_${fileIconName}`
          : null;

      const userUpdateData = {
        iconId: icon,
        updated: new Date()
      };
      await updateDoc(doc(db, 'users', newUid), userUpdateData);
      if (icon !== null) {
        uploadFile(fileIcon, icon);
      }
    }
  );
};
